// Font Configuration for Wedding Website Themes

// FontPair is not the same as Palette. Not all palette has their own font pair.
export enum FontPair {
  Bridebook = 'bridebook',
  Roses = 'roses',
  Foliage = 'foliage',
  Whimsical = 'whimsical',
  Sketches = 'sketches',
  Glamour = 'glamour',
  Timeless = 'timeless',
  Blossom = 'blossom',
  Amore = 'amore',
}
//export type FontPair = Palette;

export enum Font {
  Playfair = 'Playfair Display',
  OpenSans = 'Open Sans',
  OrelegaOne = 'Orelega One',
  Montserrat = 'Montserrat',
  Aboreto = 'Aboreto',
  Figtree = 'Figtree',
  MeowScript = 'Meow Script',
  EBGaramond = 'EB Garamond',
  // sketches
  Sacramento = 'Sacramento',
  Lora = 'Lora',
  // glamour
  // name has to be in string quotes or otherwise parsing numerical values fails and font resolves to Open Sans
  Brygada1918 = '"Brygada 1918"',

  // Timeless
  PlaywriteUSModern = 'Playwrite US Modern',
  Raleway = 'Raleway',

  // Blossom
  //Figtree = 'Figtree', // already used in roses theme
  Poppins = 'Poppins',

  // Amore
  Baskervville = 'Baskervville',
  JosefinSans = 'Josefin Sans',
}

// Each font pair is referenced by the name of the theme it was used for.
// We store this name in the database under theme.font.
export const FONT_PAIRS: Record<FontPair, { header: Font; body: Font }> = {
  [FontPair.Bridebook]: {
    header: Font.Playfair,
    body: Font.OpenSans,
  },
  [FontPair.Whimsical]: {
    header: Font.MeowScript,
    body: Font.EBGaramond,
  },
  [FontPair.Roses]: {
    header: Font.Aboreto,
    body: Font.Figtree,
  },
  [FontPair.Foliage]: {
    header: Font.OrelegaOne,
    body: Font.Montserrat,
  },
  [FontPair.Sketches]: {
    header: Font.Sacramento,
    body: Font.Lora,
  },
  [FontPair.Glamour]: {
    header: Font.EBGaramond,
    body: Font.Brygada1918,
  },
  [FontPair.Timeless]: {
    header: Font.PlaywriteUSModern,
    body: Font.Raleway,
  },
  [FontPair.Blossom]: {
    header: Font.Figtree,
    body: Font.Poppins,
  },
  [FontPair.Amore]: {
    header: Font.Baskervville,
    body: Font.JosefinSans,
  },
} as const;

export const isFontPair = (value: string): value is FontPair =>
  Object.keys(FONT_PAIRS).includes(value);

// Maps font family names (as they appear in CSS variables) to Google Fonts URLs
export const FONT_URLS: Record<Font, string> = {
  [Font.Playfair]:
    'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&display=swap',
  [Font.OpenSans]: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&display=swap',
  [Font.OrelegaOne]:
    'https://fonts.googleapis.com/css2?family=Orelega+One:wght@400;500;700&display=swap',
  [Font.Montserrat]:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;700&display=swap',
  [Font.Aboreto]: 'https://fonts.googleapis.com/css2?family=Aboreto:wght@400&display=swap',
  [Font.Figtree]: 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500&display=swap',
  [Font.MeowScript]: 'https://fonts.googleapis.com/css2?family=Meow+Script:wght@400&display=swap',
  [Font.EBGaramond]:
    'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&display=swap',
  [Font.Sacramento]: 'https://fonts.googleapis.com/css2?family=Sacramento:wght@400&display=swap',
  [Font.Lora]: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500&display=swap',
  [Font.Brygada1918]:
    'https://fonts.googleapis.com/css2?family=Brygada+1918:wght@400;500&display=swap',
  [Font.PlaywriteUSModern]:
    'https://fonts.googleapis.com/css2?family=Playwrite+US+Modern:wght@400&display=swap',
  [Font.Raleway]: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500&display=swap',
  [Font.Poppins]: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500&display=swap',
  [Font.Baskervville]:
    'https://fonts.googleapis.com/css2?family=Baskervville:wght@600&display=swap',
  [Font.JosefinSans]: 'https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400&display=swap',
} as const;

export type FontTypeface = {
  // Typography
  '--font-header-typeface': Font;
  '--font-body-typeface': Font;
  '--font-cta-typeface': Font;
};

export type FontWeight = {
  // Font weights
  '--font-header-weight': string;
  '--font-subheader-weight': string;
  '--font-body-weight': string;
  '--font-cta-weight': string;
};

export type FontSize = {
  // Mobile font sizes
  '--font-header-1-size': string;
  '--font-header-2-size': string;
  '--font-header-3-size': string;
  '--font-body-size': string;
  '--font-cta-size': string;

  // Desktop font sizes
  '--font-header-1-size-desktop': string;
  '--font-header-2-size-desktop': string;
  '--font-header-3-size-desktop': string;
  '--font-body-size-desktop': string;
  '--font-cta-size-desktop': string;
};

export type FontVariables = FontTypeface & FontWeight & FontSize;

export const TYPOGRAPHY_VARIABLES: Record<FontPair, FontVariables> = {
  [FontPair.Bridebook]: {
    // Typography
    '--font-header-typeface': Font.Playfair,
    '--font-body-typeface': Font.OpenSans,
    '--font-cta-typeface': Font.OpenSans,

    // Font weights
    '--font-header-weight': '800',
    '--font-subheader-weight': '600',
    '--font-body-weight': '400',
    '--font-cta-weight': '600',

    // Mobile font sizes
    '--font-header-1-size': '40px',
    '--font-header-2-size': '32px',
    '--font-header-3-size': '24px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '56px',
    '--font-header-2-size-desktop': '40px',
    '--font-header-3-size-desktop': '28px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Roses]: {
    // Typography
    '--font-header-typeface': Font.Aboreto,
    '--font-body-typeface': Font.Figtree,
    '--font-cta-typeface': Font.Figtree,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '500',
    '--font-body-weight': '400',
    '--font-cta-weight': '500',

    // Mobile font sizes
    '--font-header-1-size': '40px',
    '--font-header-2-size': '32px',
    '--font-header-3-size': '20px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '52px',
    '--font-header-2-size-desktop': '36px',
    '--font-header-3-size-desktop': '24px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Foliage]: {
    // Typography
    '--font-header-typeface': Font.OrelegaOne,
    '--font-body-typeface': Font.Montserrat,
    '--font-cta-typeface': Font.Montserrat,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '500',
    '--font-body-weight': '400',
    '--font-cta-weight': '400',

    // Mobile font sizes
    '--font-header-1-size': '40px',
    '--font-header-2-size': '36px',
    '--font-header-3-size': '20px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '56px',
    '--font-header-2-size-desktop': '40px',
    '--font-header-3-size-desktop': '24px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Whimsical]: {
    // Typography
    '--font-header-typeface': Font.MeowScript,
    '--font-body-typeface': Font.EBGaramond,
    '--font-cta-typeface': Font.EBGaramond,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '500',
    '--font-body-weight': '400',
    '--font-cta-weight': '600',

    // Mobile font sizes
    '--font-header-1-size': '48px',
    '--font-header-2-size': '42px',
    '--font-header-3-size': '28px',
    '--font-body-size': '18px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '74px',
    '--font-header-2-size-desktop': '60px',
    '--font-header-3-size-desktop': '38px',
    '--font-body-size-desktop': '22px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Sketches]: {
    // Typography
    '--font-header-typeface': Font.Sacramento,
    '--font-body-typeface': Font.Lora,
    '--font-cta-typeface': Font.Sacramento,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '400',
    '--font-body-weight': '400',
    '--font-cta-weight': '400',

    // Mobile font sizes
    '--font-header-1-size': '40px',
    '--font-header-2-size': '34px',
    '--font-header-3-size': '26px',
    '--font-body-size': '16px',
    '--font-cta-size': '22px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '70px',
    '--font-header-2-size-desktop': '54px',
    '--font-header-3-size-desktop': '34px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '24px',
  },
  [FontPair.Glamour]: {
    // Typography
    '--font-header-typeface': Font.EBGaramond,
    '--font-body-typeface': Font.Brygada1918,
    '--font-cta-typeface': Font.EBGaramond,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '500',
    '--font-body-weight': '400',
    '--font-cta-weight': '500',

    // Mobile font sizes
    '--font-header-1-size': '32px',
    '--font-header-2-size': '26px',
    '--font-header-3-size': '18px',
    '--font-body-size': '16px',
    '--font-cta-size': '18px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '50px',
    '--font-header-2-size-desktop': '34px',
    '--font-header-3-size-desktop': '24px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '20px',
  },
  [FontPair.Timeless]: {
    // Typography
    '--font-header-typeface': Font.PlaywriteUSModern,
    '--font-body-typeface': Font.Raleway,
    '--font-cta-typeface': Font.Raleway,

    // Font weights
    '--font-header-weight': '400',
    '--font-subheader-weight': '400',
    '--font-body-weight': '400',
    '--font-cta-weight': '500',

    // Mobile font sizes
    '--font-header-1-size': '32px',
    '--font-header-2-size': '24px',
    '--font-header-3-size': '16px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '40px',
    '--font-header-2-size-desktop': '32px',
    '--font-header-3-size-desktop': '24px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Blossom]: {
    // Typography
    '--font-header-typeface': Font.Figtree,
    '--font-body-typeface': Font.Poppins,
    '--font-cta-typeface': Font.Figtree,

    // Font weights
    '--font-header-weight': '500',
    '--font-subheader-weight': '500',
    '--font-body-weight': '400',
    '--font-cta-weight': '500',

    // Mobile font sizes
    '--font-header-1-size': '38px',
    '--font-header-2-size': '32px',
    '--font-header-3-size': '24px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '48px',
    '--font-header-2-size-desktop': '30px',
    '--font-header-3-size-desktop': '28px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
  [FontPair.Amore]: {
    // Typography
    '--font-header-typeface': Font.Baskervville,
    '--font-body-typeface': Font.JosefinSans,
    '--font-cta-typeface': Font.JosefinSans,

    // Font weights
    '--font-header-weight': '600',
    '--font-subheader-weight': '600',
    '--font-body-weight': '400',
    '--font-cta-weight': '400',

    // Mobile font sizes
    '--font-header-1-size': '32px',
    '--font-header-2-size': '30px',
    '--font-header-3-size': '24px',
    '--font-body-size': '16px',
    '--font-cta-size': '16px',

    // Desktop font sizes
    '--font-header-1-size-desktop': '48px',
    '--font-header-2-size-desktop': '40px',
    '--font-header-3-size-desktop': '28px',
    '--font-body-size-desktop': '20px',
    '--font-cta-size-desktop': '18px',
  },
} as const;
